'use client';

import { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import Head from 'next/head';

import { Input } from '@schoolerp/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from 'packages/frontend/src/schemas/auth.schema';
import ContainerImage from 'public/images/signup-container.jpg';
import BgImage from 'public/images/signup-bg.jpg';
import { useRef } from 'react';
import { useQueryClient } from 'react-query';

import { useAuth } from '../../../hooks/useAuth';
import { LoginForm } from '../../../types/auth';
import { useToast } from '../../../contexts/ToastContext';
import { LoginSkeleton } from '../../../components/skeletons/LoginSkeleton';
import { typography } from '../../../styles/design-tokens';
import { APP_CONSTANTS } from '../../../constants/app.constants';
import { nestLogoWithName } from '../../../constants/brand/brandLogos';

// Define types for settings
interface SchoolInfo {
  bannerImage?: string | null;
}

interface GeneralSettings {
  schoolInfo?: SchoolInfo;
}

interface AllSettings {
  generalSettings?: GeneralSettings;
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error } = useAuth();
  const router = useRouter();
  const [token, setToken] = useState<string | null>(null);
  const { addToast } = useToast();

  // Loading state for images
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);
  const [containerLoaded, setContainerLoaded] = useState(false);
  const [backgroundImgError, setBackgroundImgError] = useState(false);
  const [containerImgError, setContainerImgError] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const bgRef = useRef<HTMLImageElement | null>(null);

  // Get token from localStorage on client-side only
  useEffect(() => {
    // Check if we're in the browser environment
    if (typeof window !== 'undefined') {
      setToken(localStorage.getItem('accessToken'));
    }
  }, []);

  useEffect(() => {
    if (bgRef.current?.complete) setBackgroundLoaded(true);
  }, []);

  // Track screen size to decide gating behavior (lg breakpoint: 1024px)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsLargeScreen(media.matches);
    update();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', update);
    } else {
      // Safari fallback
      media.addListener(update);
    }
    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  // Track content visibility: wait for container image only on large screens
  useEffect(() => {
    if (!isLargeScreen) {
      setContentVisible(true);
      return;
    }
    if (containerLoaded || containerImgError) {
      setContentVisible(true);
    }
  }, [isLargeScreen, containerLoaded, containerImgError]);

  // Handle background image load
  const handleBackgroundLoad = () => {
    setBackgroundLoaded(true);
  };

  // Handle background image errors
  const handleBackgroundError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    console.error(
      'Failed to load background image:',
      (e.currentTarget as HTMLImageElement).src,
      e,
    );
    if (!containerImgError) {
      setBackgroundImgError(true);
      addToast('Some images could not be loaded.', 'error');
    }
  };

  // Handle container image load
  const handleContainerLoad = () => {
    setContainerLoaded(true);
  };

  // Handle container image errors
  const handleContainerError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    console.error(
      'Failed to load container image:',
      (e.currentTarget as HTMLImageElement).src,
      e,
    );
    if (!backgroundImgError) {
      setContainerImgError(true);
      addToast('Some images could not be loaded.', 'error');
    }
  };

  // Removed settings-based banner image logic (feature disabled)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const queryClient = useQueryClient();

  const onSubmit = async (data: LoginForm) => {
    try {
      // Handle case-insensitive emails by converting to lowercase
      // This ensures consistent behavior with backend email handling
      const normalizedData = {
        ...data,
        email: data.email.toLowerCase().trim(),
      };
      // Clear/Invalidate react-query cache before login to avoid showing stale data of another institute
      try {
        await queryClient.invalidateQueries();
        await queryClient.cancelQueries();
      
      } catch (e) {
        console.warn('[login] Failed to clear react-query cache:', e);
      }

      await login(normalizedData);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const disabled = true;

  return (
    <>
      {/* Skeleton loader - shown until content is ready */}
      {!contentVisible && <LoginSkeleton />}

      {/* Main content - always rendered but initially hidden */}
      <div
        className={`min-h-screen relative flex items-center justify-center p-4 ${!contentVisible ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`}
      >
        <div className="absolute inset-0">
          {/* Static background image only; dynamic banner removed */}
          {!backgroundImgError ? (
            <>
              <img
                ref={bgRef}
                src={BgImage.src}
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                onLoad={handleBackgroundLoad}
                onError={handleBackgroundError}
                style={{ visibility: backgroundLoaded ? 'visible' : 'hidden' }}
              />
            </>
          ) : (
            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500">
              <p>Background image failed to load.</p>
            </div>
          )}
          <div className="absolute inset-0 bg-[#0D373D]/50" />
        </div>
        <div className="w-full max-w-[1200px] min-h-[600px] lg:h-[720px] bg-white rounded-2xl shadow-[0_16px_50px_-12px_rgba(0,0,0,0.15)] overflow-hidden flex relative z-10">
          {/* Left Panel - Form */}
          <div className="w-full lg:w-[60%] p-8 lg:p-12 flex flex-col relative">
            <button
              type="button"
              onClick={() => router.push('/')}
              className="absolute top-6 left-6 flex items-center gap-3 group"
              aria-label={`Go back to ${APP_CONSTANTS.APP_NAME} homepage`}
            >
              <div className="p-2 rounded-xl group-hover:bg-white transition-all">
                <ArrowLeft size={20} className="text-[#4D4F52]" />
              </div>
              <span className={`${typography.text.body.default} text-[#4D4F52]`}>Back</span>
            </button>

            <div className="flex-1">
              <div className="w-full max-w-[400px] mx-auto pt-20">
                <img
                  src={nestLogoWithName.src}
                  alt={`${APP_CONSTANTS.APP_NAME} Logo`}
                  height={24}
                  className="h-6 w-auto mb-2 -ml-1"
                />
                <h1 className="text-3xl font-bold text-[#0D373D] mb-3">
                  Welcome Back
                </h1>
                <p className="text-[#4D4F52] mb-8">
                  Sign in to continue your journey
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-[#4D4F52]/50 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        {...register('email')}
                        type="text"
                        placeholder="Email address"
                        disabled={isLoading}
                        className="pl-10 w-full rounded-xl border-[#E4E8EA] focus:border-[#0D373D] focus:ring-[#0D373D]/10 placeholder-[#4D4F52]/50 py-2.5"
                      />
                    </div>
                    {errors.email?.message && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.email?.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <Lock className="w-4 h-4 text-[#4D4F52]/50 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        {...register('password')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        disabled={isLoading}
                        className="pl-10 w-full rounded-xl border-[#E4E8EA] focus:border-[#0D373D] focus:ring-[#0D373D]/10 placeholder-[#4D4F52]/50 py-2.5"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 text-[#4D4F52]/50" />
                        ) : (
                          <Eye className="w-4 h-4 text-[#4D4F52]/50" />
                        )}
                      </button>
                    </div>
                    {errors.password?.message && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.password?.message}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Link
                      href="/forgot-password"
                      className="text-sm text-[#0D373D] hover:text-[#0D373D]/80"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {error && (
                    <div className="rounded-xl bg-red-50 p-4 border border-red-100">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg
                            className="h-5 w-5 text-red-400"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 px-4 bg-[#0D373D] text-white rounded-xl hover:bg-[#0D373D]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D373D]/10 text-sm font-medium"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-r-transparent" />
                      </div>
                    ) : (
                      'Sign in'
                    )}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[#E4E8EA]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-[#4D4F52]">
                        Don't have an account?
                      </span>
                    </div>
                  </div>

                  {/* Use the commented button when allowing user to create an account we have disabled it for now */}
                  {/* <Link
                    href="/signup"
                    className="w-full flex justify-center py-3.5 px-4 border border-[#E4E8EA] rounded-xl text-sm font-medium text-[#4D4F52] bg-white hover:bg-[#F5F8F9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D373D]/10"
                  >
                    Create an account
                  </Link> */}
                  <Link
                    href={disabled ? '#' : '/signup'}
                    onClick={(e) => disabled && e.preventDefault()}
                    aria-disabled={disabled}
                    className={`w-full flex justify-center py-3.5 px-4 border rounded-xl text-sm font-medium 
    ${disabled ? 'text-gray-400 bg-gray-100 border-[#E4E8EA] cursor-not-allowed' : 'text-[#4D4F52] bg-white hover:bg-[#F5F8F9] border-[#E4E8EA]'} 
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0D373D]/10`}
                  >
                    Create an account
                  </Link>
                </form>
              </div>
            </div>
            {/* <div className="mt-6 flex items-center justify-center text-xs text-[#7A7D81]">
              <span className="mr-2">Powered by</span>
                    <img
                      src={APP_CONSTANTS.ASSETS.emmpleLogo}
                      alt="Empple logo"
                      width={20}
                      height={20}
                      className="object-contain inline-block"
                    />
              <span className="ml-2">(emmple)</span>
            </div> */}
          </div>

          {/* Right Panel - Image */}
          <div className="hidden lg:block w-[40%] relative overflow-hidden">
            {/* Right-panel uses static container image; dynamic banner removed */}
            {!containerImgError ? (
              <img
                src={ContainerImage.src}
                // alt="Happy Students"
                width={ContainerImage.width}
                height={ContainerImage.height}
                className="absolute inset-0 w-full h-full object-cover object-center !max-w-none"
                loading="eager"
                decoding="sync"
                onLoad={handleContainerLoad}
                onError={handleContainerError}
                fetchPriority="high"
              />
            ) : (
              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500">
                <p>Container image failed to load.</p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D373D]/95 via-[#0D373D]/40 to-transparent" />
            <div className="absolute inset-0 flex items-end p-12">
              <div className="relative z-10 space-y-4 max-w-[320px]">
                <h2 className="text-5xl font-bold text-white leading-tight">
                  Welcome
                  <br />
                  Back
                </h2>
                <p className="text-xl text-white/90 font-light">
                  Sign in to continue managing your educational institution with
                  ease
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Head>
        {/* Only preload the right panel (container) image so it loads with the page */}
        <link
          rel="preload"
          href="/images/signup-container.jpg"
          as="image"
          fetchPriority="high"
        />
      </Head>
    </>
  );
}
